import { makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components';
import { Checkmark16Regular } from '@fluentui/react-icons';
import { CaseMilestone } from '../../../../data/types';

const useStyles = makeStyles({
    root: {
        overflowX: 'auto',
        overflowY: 'hidden',
        padding: '1rem 0.75rem 1.25rem',
    },
    list: {
        display: 'flex',
        width: '100%',
        minWidth: 'max-content',
        margin: 0,
        padding: 0,
        listStyleType: 'none',
    },
    step: {
        position: 'relative',
        flexGrow: 1,
        flexBasis: '9rem',
        minWidth: '9rem',
        textAlign: 'center',
    },
    stepContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    connector: {
        position: 'absolute',
        zIndex: 0,
        top: '0.9375rem',
        left: '50%',
        right: '-50%',
        height: '0.1875rem',
        backgroundColor: tokens.colorNeutralStroke2,
    },
    connectorComplete: {
        backgroundColor: tokens.colorStatusSuccessBackground3,
    },
    connectorCurrent: {
        height: 0,
        borderTop: `0.1875rem dotted ${tokens.colorBrandBackground}`,
        backgroundColor: 'transparent',
    },
    marker: {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '2rem',
        height: '2rem',
        margin: '0 auto',
        boxSizing: 'border-box',
        border: `0.1875rem solid ${tokens.colorNeutralStrokeAccessible}`,
        borderRadius: tokens.borderRadiusCircular,
        color: tokens.colorNeutralForeground3,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    markerComplete: {
        borderColor: tokens.colorStatusSuccessBackground3,
        color: tokens.colorNeutralForegroundOnBrand,
        backgroundColor: tokens.colorStatusSuccessBackground3,
    },
    label: {
        display: 'block',
        marginTop: '0.5rem',
        paddingInline: '0.5rem',
        overflowWrap: 'break-word',
    },
    labelCurrent: {
        fontWeight: tokens.fontWeightSemibold,
    },
});

type MilestoneProgressProps = {
    milestones: CaseMilestone[];
};

export const MilestoneProgress = ({ milestones }: MilestoneProgressProps) => {
    const styles = useStyles();
    const currentMilestoneIndex = milestones.findIndex((milestone) => !milestone.completed);

    if (milestones.length <= 1) return null;

    return (
        <nav className={styles.root} aria-label='Milestone progress' data-testid='case-milestones-progress'>
            <ol className={styles.list}>
                {milestones.map((milestone, index) => {
                    const isComplete = !!milestone.completed;
                    const isCurrent = index === currentMilestoneIndex;
                    const nextMilestoneIsComplete = !!milestones[index + 1]?.completed;
                    const connectorIsComplete = isComplete && nextMilestoneIsComplete;
                    const connectorLeadsToCurrent = isComplete && index + 1 === currentMilestoneIndex;
                    const status = isComplete ? 'Complete' : isCurrent ? 'In progress' : 'Not started';

                    return (
                        <li
                            className={styles.step}
                            key={milestone.milestoneId ?? `${milestone.name}-${index}`}
                            aria-current={isCurrent ? 'step' : undefined}
                            aria-label={`${milestone.name || `Milestone ${index + 1}`}: ${status}`}
                        >
                            {index < milestones.length - 1 && (
                                <span
                                    className={mergeClasses(
                                        styles.connector,
                                        connectorIsComplete && styles.connectorComplete,
                                        connectorLeadsToCurrent && styles.connectorCurrent,
                                    )}
                                    aria-hidden='true'
                                />
                            )}
                            <div className={styles.stepContent}>
                                <span
                                    className={mergeClasses(
                                        styles.marker,
                                        isComplete && styles.markerComplete,
                                    )}
                                    aria-hidden='true'
                                >
                                    {isComplete && <Checkmark16Regular />}
                                </span>
                                <Text className={mergeClasses(styles.label, isCurrent && styles.labelCurrent)}>
                                    {milestone.name || `Milestone ${index + 1}`}
                                </Text>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};
